import { getRuntimeConfig } from "@/app/config/runtime-config";
import type { AdminAuthRealmConfig, RuntimeConfig } from "@/types/clm-web";

const ADMIN_HOSTNAME_PREFIX = "admin.";

export type ActiveAuthRealm = "standard" | "admin";

/**
 * Resolved realm context for the current browser origin.
 *
 * - `standard`: the default realm served from `appBaseUrl` (or any unmatched origin).
 * - `admin`: the origin matches `authRealms.admin.spaOrigin` exactly.
 * - `admin-unavailable`: the host looks like an admin host, but no admin realm is
 *   configured (or the configured admin origin does not match). This is a
 *   fail-closed state: no auth calls may be made and no app shell may render.
 */
export type RealmContext =
  | { realm: "standard"; authBaseUrl: string }
  | { realm: "admin"; authBaseUrl: string; adminRealm: AdminAuthRealmConfig }
  | { realm: "admin-unavailable" };

export function isAdminStyleHostname(hostname: string): boolean {
  return hostname.toLowerCase().startsWith(ADMIN_HOSTNAME_PREFIX);
}

export function getBrowserOrigin(): string {
  return typeof window !== "undefined" ? window.location.origin : "";
}

export function resolveRealmContext(config: RuntimeConfig, origin: string): RealmContext {
  const originUrl = parseOrigin(origin);
  const adminRealm = config.authRealms?.admin;

  if (adminRealm && originUrl && originUrl.origin === new URL(adminRealm.spaOrigin).origin) {
    return { realm: "admin", authBaseUrl: adminRealm.authBaseUrl, adminRealm };
  }

  if (originUrl && isAdminStyleHostname(originUrl.hostname)) {
    return { realm: "admin-unavailable" };
  }

  return { realm: "standard", authBaseUrl: config.authBaseUrl };
}

export function resolveActiveRealmContext(
  config: RuntimeConfig = getRuntimeConfig(),
  origin: string = getBrowserOrigin(),
): RealmContext {
  return resolveRealmContext(config, origin);
}

export function detectActiveRealm(config: RuntimeConfig, origin: string): ActiveAuthRealm {
  return resolveRealmContext(config, origin).realm === "admin" ? "admin" : "standard";
}

export function resolveRealmAuthBaseUrl(context: RealmContext): string {
  if (context.realm === "admin-unavailable") {
    throw new Error("Admin access is not available on this host.");
  }

  return context.authBaseUrl;
}

function parseOrigin(origin: string): URL | undefined {
  if (typeof origin !== "string" || origin.trim().length === 0) {
    return undefined;
  }

  try {
    return new URL(origin);
  } catch {
    return undefined;
  }
}
